import React, { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const formatRupee = (value) => {
  const val = Number(value) || 0;
  return `₹${val.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: val % 1 === 0 ? 0 : 2,
  })}`;
};

const AdsReportTable = ({
  data = [],
  page = 1,
  limit = 20,
  loading = false,
  orderBy = 'date',
  orderDir = 'DESC',
  onSortChange,
  columnFilters = {},
  onColumnFilterChange,
  onApplyFilters,
  filterOptions = { platforms: [], months: [] },
}) => {
  const tableContainerRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);

  // Double scroll synchronization
  useEffect(() => {
    const updateScrollbar = () => {
      if (tableContainerRef.current && topScrollbarContentRef.current) {
        const tableWidth = tableContainerRef.current.scrollWidth;
        topScrollbarContentRef.current.style.width = `${tableWidth}px`;
      }
    };
    updateScrollbar();
    window.addEventListener('resize', updateScrollbar);
    return () => window.removeEventListener('resize', updateScrollbar);
  }, [data]);

  const isSyncingScrollRef = useRef(false);
  const lastTableScrollLeftRef = useRef(0);

  const syncScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = tableContainerRef.current.scrollLeft;
      if (currentLeft === lastTableScrollLeftRef.current) return;
      lastTableScrollLeftRef.current = currentLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      topScrollbarRef.current.scrollLeft = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollbarRef.current) {
      const currentLeft = topScrollbarRef.current.scrollLeft;
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableContainerRef.current.scrollLeft = currentLeft;
      lastTableScrollLeftRef.current = currentLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    }
  };

  const handleHeaderClick = (columnKey) => {
    if (orderBy === columnKey) {
      const nextDir = orderDir === 'ASC' ? 'DESC' : 'ASC';
      onSortChange && onSortChange(columnKey, nextDir);
    } else {
      onSortChange && onSortChange(columnKey, 'DESC');
    }
  };

  const getSortIcon = (columnKey) => {
    if (orderBy !== columnKey) return <FontAwesomeIcon icon={faSort} style={{ opacity: 0.35, marginLeft: 5 }} />;
    return orderDir === 'ASC' ? (
      <FontAwesomeIcon icon={faSortUp} style={{ color: '#007bff', marginLeft: 5 }} />
    ) : (
      <FontAwesomeIcon icon={faSortDown} style={{ color: '#007bff', marginLeft: 5 }} />
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onApplyFilters && onApplyFilters();
    }
  };

  return (
    <div className="panel panel-default with-rounded-corner with-shadow" style={{ marginBottom: 0 }}>
      {/* Double Scroll Container */}
      <div className="large-table-container-3">
        {/* Top Scrollbar */}
        <div
          ref={topScrollbarRef}
          onScroll={handleTopScroll}
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
            height: '14px',
            marginBottom: '1px',
            background: '#f8f9fa',
          }}
        >
          <div ref={topScrollbarContentRef} style={{ height: '1px' }}></div>
        </div>

        {/* Table Container */}
        <div
          ref={tableContainerRef}
          onScroll={syncScroll}
          style={{
            overflowY: 'auto',
            height: 'calc(112vh - 280px)',
            minHeight: '380px',
            overflowX: 'auto',
            position: 'relative',
          }}
        >
          <table className="table table-condensed table-hover bg-white sticky-table">
            <thead>
              {/* Header Sort Row */}
              <tr style={{ cursor: 'pointer' }}>
                <th style={{ minWidth: '110px' }} onClick={() => handleHeaderClick('date')}>
                  Date {getSortIcon('date')}
                </th>
                <th style={{ minWidth: '100px' }} onClick={() => handleHeaderClick('platform')}>
                  Platform {getSortIcon('platform')}
                </th>
                <th style={{ minWidth: '200px' }} onClick={() => handleHeaderClick('campaign')}>
                  Campaign {getSortIcon('campaign')}
                </th>
                <th style={{ minWidth: '180px' }} onClick={() => handleHeaderClick('campaign_name')}>
                  Campaign Name {getSortIcon('campaign_name')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '110px' }} onClick={() => handleHeaderClick('cost')}>
                  Cost {getSortIcon('cost')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '90px' }} onClick={() => handleHeaderClick('leads')}>
                  Leads {getSortIcon('leads')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '90px' }} onClick={() => handleHeaderClick('cpl')}>
                  CPL {getSortIcon('cpl')}
                </th>
                {/* <th style={{ textAlign: 'right', minWidth: '90px' }}>CPL</th> */}
                <th style={{ textAlign: 'right', minWidth: '90px' }} onClick={() => handleHeaderClick('likely')}>
                  Likely {getSortIcon('likely')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '90px' }} onClick={() => handleHeaderClick('sales')}>
                  Sales {getSortIcon('sales')}
                </th>
                <th style={{ textAlign: 'right', minWidth: '120px' }} onClick={() => handleHeaderClick('revenue')}>
                  Revenue {getSortIcon('revenue')}
                </th>
                <th style={{ minWidth: '100px' }} onClick={() => handleHeaderClick('month')}>
                  Month {getSortIcon('month')}
                </th>
              </tr>

              {/* Inline Filter Row */}
              <tr>
                <th></th>
                <th>
                  <select
                    className="form-control input-sm"
                    style={{ height: '28px', padding: '2px 5px', fontSize: '11.5px' }}
                    value={columnFilters.platform || ''}
                    onChange={(e) => {
                      onColumnFilterChange && onColumnFilterChange('platform', e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    <option value="Google">Google</option>
                    <option value="Meta">Meta</option>
                    {filterOptions.platforms
                      ?.filter((p) => p !== 'Google' && p !== 'Meta')
                      .map((p, idx) => (
                        <option key={idx} value={p}>
                          {p}
                        </option>
                      ))}
                  </select>
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search campaign..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px' }}
                    value={columnFilters.campaign || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('campaign', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search campaign name..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px' }}
                    value={columnFilters.campaign_name || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('campaign_name', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Cost..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.cost || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('cost', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Leads..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.leads || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('leads', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min CPL..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.cpl || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('cpl', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Likely..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.likely || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('likely', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Sales..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.sales || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('sales', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Min Rev..."
                    style={{ height: '28px', padding: '2px 6px', fontSize: '11.5px', textAlign: 'right' }}
                    value={columnFilters.revenue || ''}
                    onChange={(e) => onColumnFilterChange && onColumnFilterChange('revenue', e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </th>
                <th>
                  <select
                    className="form-control input-sm"
                    style={{ height: '28px', padding: '2px 5px', fontSize: '11.5px' }}
                    value={columnFilters.month || ''}
                    onChange={(e) => {
                      onColumnFilterChange && onColumnFilterChange('month', e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {filterOptions.months?.map((m, idx) => (
                      <option key={idx} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 10 }).map((_, rIdx) => (
                  <tr key={`skeleton-${rIdx}`}>
                    <td><div className="skeleton-text" style={{ width: '80px', height: '14px' }}></div></td>
                    <td><div className="skeleton-text" style={{ width: '60px', height: '14px' }}></div></td>
                    <td><div className="skeleton-text" style={{ width: '150px', height: '14px' }}></div></td>
                    <td><div className="skeleton-text" style={{ width: '130px', height: '14px' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '70px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '40px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '50px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '40px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '40px', height: '14px', float: 'right' }}></div></td>
                    <td style={{ textAlign: 'right' }}><div className="skeleton-text" style={{ width: '80px', height: '14px', float: 'right' }}></div></td>
                    <td><div className="skeleton-text" style={{ width: '60px', height: '14px' }}></div></td>
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center text-muted" style={{ padding: '30px 15px' }}>
                    No ad records found.
                  </td>
                </tr>
              ) : (
                data.map((row) => {
                  const formattedDate = row.date ? moment(row.date).format('DD-MM-YYYY') : '-';
                  return (
                    <tr key={row.id}>
                      <td>{formattedDate}</td>
                      <td>{row.platform || '-'}</td>
                      <td>{row.campaign || '-'}</td>
                      <td style={{ fontWeight: 500, color: '#1e293b' }}>{row.campaign_name || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{formatRupee(row.cost)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.leads || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {row.cpl > 0 ? formatRupee(row.cpl) : '-'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.likely || 0).toLocaleString('en-IN')}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.sales || 0).toLocaleString('en-IN')}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatRupee(row.revenue)}
                      </td>
                      <td>{row.month || '-'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdsReportTable;
